import React, { useContext, useEffect, useRef, useState } from 'react';
import { MdSearch, MdUploadFile, MdClose, MdArrowDropDown, MdOutlineChatBubbleOutline } from 'react-icons/md';
import type { FillAppField as FieldDef, FillAppLabelLink, FillAppLabelBlock, FillAppValues, ChoiceGroups, ChoiceOption, FillAppFieldComment } from 'global/fillapp';
import { WORD_LIMITS, WORD_MINIMUMS, wordCount, fieldCharLimit } from 'global/fillapp';
import { FillAppCommentsContext, type StudentFillAppCommentsCtx } from './commentsContext';
import CoursesGradesWidget from 'components/CoursesGradesWidget';
import { DIAL_CODES } from 'global/dialCodes';
import { useFillAppDirectory, type DirectoryRow } from 'hooks/useFillAppDirectory';

import './index.css';

// Wraps each help link's text ("Learn more") in a clickable anchor, matching
// the real Common App labels. First case-insensitive occurrence only.
function renderLabel(label: string, links?: FillAppLabelLink[]): React.ReactNode {
    if (!links || links.length === 0) return label;
    let parts: React.ReactNode[] = [label];
    links.forEach((link, li) => {
        let done = false;
        const next: React.ReactNode[] = [];
        for (const part of parts) {
            if (done || typeof part !== 'string') { next.push(part); continue; }
            const idx = part.toLowerCase().indexOf(link.text.toLowerCase());
            if (idx === -1) { next.push(part); continue; }
            done = true;
            if (idx > 0) next.push(part.slice(0, idx));
            next.push(
                <a
                    key={`lnk${li}`}
                    className='ffield__labelLink'
                    href={link.href}
                    target='_blank'
                    rel='noopener noreferrer'
                >
                    {part.slice(idx, idx + link.text.length)}
                </a>,
            );
            if (idx + link.text.length < part.length) next.push(part.slice(idx + link.text.length));
        }
        parts = next;
    });
    return parts;
}

// Labels that carry a bullet list (household independence criteria, fee-waiver
// eligibility) arrive pre-structured as blocks. Rendering the flat string would
// run the bullets together into one paragraph, which is how Common App's own
// markup reads once you strip the <ul>.
function renderLabelBlocks(
    blocks: FillAppLabelBlock[],
    links?: FillAppLabelLink[],
    trailing?: React.ReactNode,
): React.ReactNode {
    // `trailing` (the required asterisk) has to sit INSIDE the last paragraph.
    // Rendered after the blocks it would follow a <p>, which is block-level, so
    // the marker dropped onto a line of its own.
    const lastPara = blocks.map((b, i) => (b.type === 'p' ? i : -1))
        .reduce((acc, i) => (i > acc ? i : acc), -1);
    const out = blocks.map((b, i) => (
        b.type === 'ul' ? (
            <ul key={i} className='ffield__labelList'>
                {b.items.map((item, j) => <li key={j}>{renderLabel(item, links)}</li>)}
            </ul>
        ) : (
            <p key={i} className='ffield__labelPara'>
                {renderLabel(b.text, links)}
                {i === lastPara && trailing}
            </p>
        )
    ));
    // No paragraph to hang it on (a label that is only a list) — give it one.
    return lastPara === -1 && trailing
        ? [...out, <p key='req' className='ffield__labelPara'>{trailing}</p>]
        : out;
}


type Props = {
    field: FieldDef;
    value: unknown;
    values?: FillAppValues;   // full form values (for optionsFilter triggers)
    cycleYear?: number;       // application cycle (school-year options)
    choiceGroups: ChoiceGroups;
    readOnly?: boolean;
    onChange?: (qid: string, value: unknown) => void;
    onBlur?: (qid: string) => void;
};

function stripHtml(s: string): string {
    return s.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
}

// Phone values are stored as "<dial> <digits>" (e.g. "+1 5551234567"); empty = ''.
function parsePhone(v: string): { dial: string; number: string } {
    const m = v.match(/^(\+\d{1,4})\s*(.*)$/);
    if (m) return { dial: m[1], number: m[2] };
    return { dial: '+1', number: v };
}

// Address values are stored as an object of subfields; empty = ''.
const ADDRESS_PARTS: { key: string; label: string; narrow?: boolean }[] = [
    { key: 'line1', label: 'Address line 1' },
    { key: 'line2', label: 'Address line 2' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State/Province' },
    { key: 'zip', label: 'ZIP/Postal code', narrow: true },
    { key: 'country', label: 'Country' },
];

function parseAddress(value: unknown): Record<string, string> {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        const out: Record<string, string> = {};
        for (const p of ADDRESS_PARTS) out[p.key] = String((value as Record<string, unknown>)[p.key] ?? '');
        return out;
    }
    return Object.fromEntries(ADDRESS_PARTS.map(p => [p.key, '']));
}

// "Sofia Rivera" -> "SR"; single word -> first letter. Fallback handled by caller.
function commentInitials(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return 'T';
    return words.slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

function commentTimeAgo(iso: string): string {
    const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return `${Math.floor(hr / 24)}d ago`;
}

// Tutor feedback on this field: all comments together in one list — open ones
// carry a "Mark resolved" action, resolved ones render muted with a chip.
function StudentFieldComments({ comments, ctx }: {
    comments: FillAppFieldComment[];
    ctx: StudentFillAppCommentsCtx;
}) {
    const [busyId, setBusyId] = useState<number | null>(null);

    return (
        <div className='ffield__stuComments'>
            {comments.map(c => (
                <div key={c.id} className={`ffield__stuComment ${c.isResolved ? 'ffield__stuComment--resolved' : ''}`}>
                    <div className='ffield__stuCommentMeta'>
                        <span className='ffield__stuCommentAva' aria-hidden='true'>
                            {c.authorName ? commentInitials(c.authorName) : 'T'}
                        </span>
                        <span className='ffield__stuCommentWho'>{c.authorName || 'Your tutor'}</span>
                        <span className='ffield__stuCommentWhen'>{commentTimeAgo(c.createdAt)}</span>
                    </div>
                    <p className='ffield__stuCommentBody'>{c.body}</p>
                    <div className='ffield__stuCommentActions'>
                        {c.isResolved ? (
                            <span className='ffield__stuResolvedChip'>Resolved</span>
                        ) : (
                            <button
                                type='button'
                                disabled={busyId === c.id}
                                onClick={() => {
                                    setBusyId(c.id);
                                    ctx.resolveComment(c.id).finally(() => setBusyId(null));
                                }}
                            >
                                {busyId === c.id ? 'Resolving…' : 'Mark resolved'}
                            </button>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

function FillAppField({ field, value, values, cycleYear, choiceGroups, readOnly, onChange, onBlur }: Props) {
    // Privacy: sensitive fields (SSN, visa number, …) are shown so the form
    // mirrors the real Common App, but never fillable here.
    const disabled = !!readOnly || !!field.sensitive;
    const allChoices = field.choiceGroup ? choiceGroups[field.choiceGroup] : undefined;
    // Options narrowed by another answer (e.g. course schedule terms by the
    // selected scheduling system). Map keys are lowercase while the trigger
    // radio stores capitalized export codes ("Semester"), so normalize. An
    // unanswered trigger allows nothing — matching Common App, where the
    // schedule dropdown is empty until a scheduling system is chosen.
    let choices = allChoices;
    if (allChoices && field.optionsFilter && values) {
        const key = String(values[field.optionsFilter.qid] ?? '').toLowerCase();
        const allowed = field.optionsFilter.map[key] ?? [];
        choices = allChoices.filter(c => allowed.includes(String(c.exportCode)));
    }
    const v = value == null ? '' : String(value);

    const handleChange = (next: unknown) => {
        if (!disabled && onChange) onChange(field.qid, next);
    };
    const handleBlur = () => {
        if (!disabled && onBlur) onBlur(field.qid);
    };

    const commentsCtx = useContext(FillAppCommentsContext);
    const fieldComments = commentsCtx?.commentsByQid[field.qid] || [];
    const hasOpenComments = fieldComments.some(c => !c.isResolved);
    // Panel visibility, toggled by the label-row button (same placement as the
    // tutor side). Defaults open while feedback is pending.
    const [commentsOpen, setCommentsOpen] = useState(hasOpenComments);
    const rawLabel = stripHtml(field.label || '');
    const label = rawLabel.replace(/\s*\*\s*$/, '');
    const isNarrow = field.type === 'Date' || field.type === 'Month' || field.qid === 'q_176';

    // Helper text harvested from the Common App captures (control__hint).
    // Radios render it between the options and the Clear button, like Common App.
    const hintNode = field.hint
        ? <div className='ffield__hintText'>{field.hint}</div>
        : null;

    // Common App limits: live counter + overage flag, but never block typing —
    // students should be able to draft long and trim later.
    const isTextEntry = field.type === 'Short' || field.type === 'Text'
        || field.type === 'Email' || field.type === 'Long';
    const wordLimit = isTextEntry ? WORD_LIMITS[field.qid] : undefined;
    const wordMin = isTextEntry ? WORD_MINIMUMS[field.qid] : undefined;
    const charLimit = isTextEntry && !wordLimit ? fieldCharLimit(field) : null;
    const words = wordLimit ? wordCount(v) : 0;
    const isOver = wordLimit ? words > wordLimit : charLimit != null && v.length > charLimit;
    const showCounter = (!!wordLimit || charLimit != null) && (field.type === 'Long' || v.length > 0);
    const overClass = isOver ? 'ffield__input--over' : '';
    const counterNode = showCounter
        ? (
            <div className={`ffield__count ${isOver ? 'ffield__count--over' : ''}`}>
                {wordLimit
                    ? `${words} / ${wordLimit} words`
                    : `${v.length} / ${charLimit} characters`}
                {isOver && ' — over limit'}
            </div>
        )
        : null;
    // "Min: 250 / Max: 650" (live-form wording) sits left-aligned right under
    // the essay box, with the live counter on the same row's right edge.
    const limitsNode = wordMin
        ? (
            <div className='ffield__limitRow'>
                <span className='ffield__minMax'>{`Min: ${wordMin} / Max: ${wordLimit}`}</span>
                {counterNode}
            </div>
        )
        : counterNode;

    let control: React.ReactNode;
    switch (field.type) {
        case 'Short':
        case 'Text':
        case 'Email':
            control = (
                <input
                    className={`ffield__input ${isNarrow ? 'ffield__input--narrow' : ''} ${overClass}`}
                    type={field.type === 'Email' ? 'email' : 'text'}
                    value={v}
                    disabled={disabled}
                    onChange={e => handleChange(e.target.value)}
                    onBlur={handleBlur}
                />
            );
            break;
        case 'Phone': {
            // Country dial-code dropdown + digits-only number, like ca-phone.
            const phone = parsePhone(v);
            const emit = (dial: string, number: string) => {
                handleChange(number === '' ? '' : `${dial} ${number}`);
            };
            control = (
                <div>
                    <div className='ffield__phone'>
                        <select
                            className='ffield__select ffield__phoneCode'
                            value={phone.dial}
                            disabled={disabled}
                            aria-label='Country code'
                            onChange={e => emit(e.target.value, phone.number)}
                            onBlur={handleBlur}
                        >
                            {DIAL_CODES.map((c, i) => (
                                <option key={i} value={c.dial}>{c.dial} {c.name}</option>
                            ))}
                        </select>
                        <input
                            className='ffield__input ffield__phoneNumber'
                            type='tel'
                            inputMode='numeric'
                            value={phone.number}
                            disabled={disabled}
                            aria-label='Phone number'
                            onChange={e => emit(phone.dial, e.target.value.replace(/\D/g, ''))}
                            onBlur={handleBlur}
                        />
                    </div>
                </div>
            );
            break;
        }
        case 'CollegeLookup':
        case 'SchoolLookup': {
            control = (
                <LookupControl
                    kind={field.type === 'SchoolLookup' ? 'schools' : 'colleges-all'}
                    noun={field.type === 'SchoolLookup' ? 'school' : 'college'}
                    value={v}
                    disabled={disabled}
                    onCommit={name => { handleChange(name); handleBlur(); }}
                />
            );
            break;
        }
        case 'Upload': {
            control = (
                <UploadControl
                    value={v}
                    disabled={disabled}
                    onPick={name => { handleChange(name); handleBlur(); }}
                />
            );
            break;
        }
        case 'CoursesGrades': {
            // Transcript-entry dialog ("Add 9th grade"); grade name from the
            // label "Add <grade> courses ...".
            const gm = label.match(/^Add (.+?) courses/i);
            control = (
                <CoursesGradesWidget
                    qid={field.qid}
                    gradeLabel={gm ? gm[1] : 'courses'}
                    cycleYear={cycleYear}
                    value={value}
                    values={values || {}}
                    choiceGroups={choiceGroups}
                    readOnly={disabled}
                    onChange={onChange}
                    onBlur={onBlur}
                />
            );
            break;
        }
        case 'Address': {
            const addr = parseAddress(value);
            const emit = (key: string, val: string) => {
                const next = { ...addr, [key]: val };
                const allEmpty = ADDRESS_PARTS.every(p => (next[p.key] || '').trim() === '');
                handleChange(allEmpty ? '' : next);
            };
            control = (
                <div className='ffield__address'>
                    {ADDRESS_PARTS.map(p => (
                        <label key={p.key} className={`ffield__addressPart ${p.narrow ? 'ffield__addressPart--narrow' : ''}`}>
                            <span className='ffield__addressLabel'>{p.label}</span>
                            <input
                                className='ffield__input'
                                type='text'
                                value={addr[p.key]}
                                disabled={disabled}
                                onChange={e => emit(p.key, e.target.value)}
                                onBlur={handleBlur}
                            />
                        </label>
                    ))}
                </div>
            );
            break;
        }
        case 'Long':
            control = (
                <textarea
                    className={`ffield__textarea ${overClass}`}
                    value={v}
                    disabled={disabled}
                    onChange={e => handleChange(e.target.value)}
                    onBlur={handleBlur}
                />
            );
            break;
        case 'Date':
            control = (
                <input
                    className='ffield__input ffield__input--narrow'
                    type='date'
                    value={v}
                    disabled={disabled}
                    onChange={e => handleChange(e.target.value)}
                    onBlur={handleBlur}
                />
            );
            break;
        case 'Month':
            control = (
                <input
                    className='ffield__input ffield__input--narrow'
                    type='month'
                    value={v}
                    disabled={disabled}
                    onChange={e => handleChange(e.target.value)}
                    onBlur={handleBlur}
                />
            );
            break;
        case 'SingleSelectDropdown':
        case 'CollegeLookup':
        case 'SchoolLookup':
            control = (
                <select
                    className={`ffield__select ${isNarrow ? 'ffield__select--narrow' : ''}`}
                    value={v}
                    disabled={disabled}
                    onChange={e => handleChange(e.target.value)}
                    onBlur={handleBlur}
                >
                    <option value=''>— Select —</option>
                    {(choices || []).map((c, i) => (
                        <option key={i} value={c.exportCode}>{c.label}</option>
                    ))}
                </select>
            );
            break;
        case 'MultiSelectDropdown': {
            const arrVal = Array.isArray(value) ? (value as unknown[]).map(String) : (v ? v.split(',') : []);
            control = (
                <MultiSelect
                    selected={arrVal}
                    choices={choices || []}
                    labelChoices={allChoices || []}
                    disabled={disabled}
                    onChange={next => handleChange(next)}
                    onClose={handleBlur}
                />
            );
            break;
        }
        case 'Radio':
            // Radios can't be natively deselected, so (matching the Common App)
            // always offer a "Clear answer" button below the options.
            control = (
                <>
                    <div className='ffield__radioGroup'>
                        {(choices || []).map((c, i) => {
                            const selected = v === String(c.exportCode);
                            return (
                                <label
                                    key={i}
                                    className={`ffield__radioOption ${selected ? 'ffield__radioOption--selected' : ''} ${disabled ? 'disabled' : ''}`}
                                >
                                    <input
                                        className='ffield__radioInput'
                                        type='radio'
                                        name={field.qid}
                                        value={c.exportCode}
                                        checked={selected}
                                        disabled={disabled}
                                        onChange={() => handleChange(c.exportCode)}
                                        onBlur={handleBlur}
                                    />
                                    <span className='ffield__radioDot' />
                                    <span>{c.label}</span>
                                </label>
                            );
                        })}
                    </div>
                    {hintNode}
                    {!disabled && (
                        <button
                            type='button'
                            className='ffield__clear'
                            onClick={() => { handleChange(''); handleBlur(); }}
                        >
                            Clear answer
                        </button>
                    )}
                </>
            );
            break;
        case 'Checkbox': {
            const arrVal = Array.isArray(value) ? (value as string[]) : (v ? v.split(',') : []);
            control = (
                <div className='ffield__checkboxGroup'>
                    {(choices || []).map((c, i) => {
                        const checked = arrVal.includes(String(c.exportCode));
                        return (
                            <label key={i} className='ffield__checkboxOption'>
                                <input
                                    type='checkbox'
                                    checked={checked}
                                    disabled={disabled}
                                    onChange={() => {
                                        const next = checked
                                            ? arrVal.filter(x => x !== String(c.exportCode))
                                            : [...arrVal, String(c.exportCode)];
                                        handleChange(next);
                                    }}
                                    onBlur={handleBlur}
                                />
                                <span>{c.label}</span>
                            </label>
                        );
                    })}
                </div>
            );
            break;
        }
        default:
            control = (
                <input
                    className='ffield__input'
                    type='text'
                    value={v}
                    disabled={disabled}
                    placeholder={field.type ? `(${field.type})` : ''}
                    onChange={e => handleChange(e.target.value)}
                    onBlur={handleBlur}
                />
            );
    }

    // Display-only text rows (ca-label): no input, no required marker.
    if (field.type === 'Label') {
        return (
            <div className='ffield'>
                <div className='ffield__labelText'>
                    {field.labelBlocks
                        ? renderLabelBlocks(field.labelBlocks, field.labelLinks)
                        : <p>{renderLabel(label, field.labelLinks)}</p>}
                </div>
            </div>
        );
    }

    return (
        <div className={`ffield ${hasOpenComments ? 'ffield--commented' : ''}`}>
            {field.boldLead?.block && (
                <div className='ffield__subheader'>{field.boldLead.text}</div>
            )}
            <div className='ffield__label'>
                <span>
                    {field.boldLead && !field.boldLead.block && (
                        <b>{field.boldLead.text}&nbsp;</b>
                    )}
                    {field.labelBlocks
                        ? renderLabelBlocks(
                            field.labelBlocks, field.labelLinks,
                            field.required
                                ? <span className='ffield__required'>&nbsp;*</span>
                                : undefined)
                        : <>
                            {renderLabel(label, field.labelLinks)}
                            {field.required && <span className='ffield__required'>&nbsp;*</span>}
                        </>}
                </span>
                {commentsCtx && fieldComments.length > 0 && (
                    <button
                        type='button'
                        className={`ffield__commentBtn ${hasOpenComments ? 'ffield__commentBtn--open' : 'ffield__commentBtn--done'}`}
                        onClick={() => setCommentsOpen(o => !o)}
                    >
                        <MdOutlineChatBubbleOutline size={13} />
                        {/* Count OPEN notes only, matching the card/sidebar badges;
                            all-resolved shows the green chip without a number. */}
                        {hasOpenComments
                            ? fieldComments.filter(c => !c.isResolved).length
                            : 'Resolved'}
                    </button>
                )}
            </div>
            {control}
            {field.sensitive && (
                <div className='ffield__privacyNote'>
                    For your privacy, College Apps doesn't collect this — you'll fill it in on the
                    real Common App.
                </div>
            )}
            {/* Hint sits directly under the input, before the character
                counter — matching Common App's mat-hint placement. */}
            {field.type !== 'Radio' && hintNode}
            {limitsNode}
            {commentsCtx && commentsOpen && fieldComments.length > 0 && (
                <StudentFieldComments comments={fieldComments} ctx={commentsCtx} />
            )}
        </div>
    );
}

// ng-select-style multi-select: a dropdown with searchable checkbox options
// and removable chips for the selections (like "List countries of citizenship").
function MultiSelect({ selected, choices, labelChoices, disabled, onChange, onClose }: {
    selected: string[];
    choices: ChoiceOption[];          // selectable options (may be filtered)
    labelChoices?: ChoiceOption[];    // full list, for labeling stale selections
    disabled: boolean;
    onChange: (next: string[]) => void;
    onClose: () => void;
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                setOpen(false);
                setQuery('');
                onClose();
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open, onClose]);

    const labelOf = (code: string) =>
        (labelChoices ?? choices).find(c => String(c.exportCode) === code)?.label ?? code;
    const toggleOpt = (code: string) => {
        onChange(selected.includes(code) ? selected.filter(x => x !== code) : [...selected, code]);
    };
    const filtered = choices.filter(c => c.label.toLowerCase().includes(query.toLowerCase()));

    return (
        <div className='ffield__msel' ref={rootRef}>
            <div
                className={`ffield__mselControl ${disabled ? 'ffield__mselControl--disabled' : ''}`}
                role='button'
                tabIndex={disabled ? -1 : 0}
                aria-haspopup='listbox'
                aria-expanded={open}
                onClick={() => {
                    if (disabled) return;
                    if (open) { setOpen(false); setQuery(''); onClose(); } else setOpen(true);
                }}
                onKeyDown={e => {
                    if (disabled) return;
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); }
                }}
            >
                {selected.length === 0 ? (
                    <span className='ffield__mselPlaceholder'>- Choose one or more options -</span>
                ) : (
                    <span className='ffield__mselChips'>
                        {selected.map(code => (
                            <span key={code} className='ffield__mselChip'>
                                {labelOf(code)}
                                {!disabled && (
                                    <span
                                        className='ffield__mselChipX'
                                        role='button'
                                        aria-label={`Remove ${labelOf(code)}`}
                                        onClick={e => { e.stopPropagation(); toggleOpt(code); onClose(); }}
                                    >
                                        <MdClose />
                                    </span>
                                )}
                            </span>
                        ))}
                    </span>
                )}
                <MdArrowDropDown className='ffield__mselCaret' />
            </div>
            {open && !disabled && (
                <div className='ffield__mselPanel'>
                    <input
                        className='ffield__mselSearch'
                        type='text'
                        placeholder='Search...'
                        value={query}
                        autoFocus
                        onChange={e => setQuery(e.target.value)}
                    />
                    <div className='ffield__mselOpts' role='listbox' aria-multiselectable>
                        {filtered.map((c, i) => {
                            const code = String(c.exportCode);
                            return (
                                <label key={i} className='ffield__mselOpt'>
                                    <input
                                        type='checkbox'
                                        checked={selected.includes(code)}
                                        onChange={() => toggleOpt(code)}
                                    />
                                    <span>{c.label}</span>
                                </label>
                            );
                        })}
                        {filtered.length === 0 && (
                            <div className='ffield__mselEmpty'>No options found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// File picker that records only the chosen filename (practice form — no storage).
// The school/college lookup, modeled on the real Common App's dialog (see
// docs/fillapp/plans/fillapp-lookup-fields-plan.md and the capture in
// fillapp/Common App Screens/education/current-high-school/high-school-lookup.html):
// a "Find" button opens a popup with search-by modes (Name / City / State — CEEB
// is College Board proprietary and Country is moot for a US-only directory),
// results over the bundled NCES directory, and the two fixed escape hatches the
// real form has — Homeschooled and "I don't see my school on this list" — both
// leading to manual name + address entry. The committed value stays ONE string
// (directory pick = the name; manual = "Name, Address, City, ST Zip"), so the
// tutor view, autosave and legacy values are untouched.
type LookupMode = 'name' | 'city' | 'state';

const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL',
    'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE',
    'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD',
    'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'AS', 'GU', 'MP', 'PR', 'VI',
];

// Manual-entry school types — extracted live from the real form's School
// Information page (capture: education/current-high-school/school-manual-entry.html).
const SCHOOL_TYPES = ['Public', 'Charter', 'Religious', 'Home school', 'Independent'];

const CA_PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];

// How many rows the dialog renders at once. The full directory shows from the
// moment the dialog opens (like the real form); rendering all 36k rows would
// freeze the page, so it's a capped window with a "type to narrow" footer.
const LOOKUP_PAGE = 150;

function LookupControl({ kind, noun, value, disabled, onCommit }: {
    kind: 'schools' | 'colleges-all';
    noun: string;
    value: string;
    disabled: boolean;
    onCommit: (name: string) => void;
}) {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState<LookupMode>('name');
    const [query, setQuery] = useState('');
    // null = searching; otherwise the manual pane ('homeschool' | 'notlisted')
    const [manual, setManual] = useState<null | 'homeschool' | 'notlisted'>(null);
    const emptyManual = { name: '', type: '', country: '', line1: '', line2: '', line3: '', city: '', region: '', postal: '' };
    const [m, setM] = useState(emptyManual);
    const { rows, error, retry } = useFillAppDirectory(kind, open);

    const openDialog = () => {
        setQuery(''); setMode('name'); setManual(null);
        setM(emptyManual);
        retry();
        setOpen(true);
    };

    const openManual = (which: 'homeschool' | 'notlisted') => {
        // Extracted from the live form: country defaults to the US, and the
        // homeschool path prefills the name "Homeschooled" (editable) and drops
        // the School Type field entirely — homeschool IS the type.
        setM({
            ...emptyManual,
            country: 'United States',
            name: which === 'homeschool' ? 'Homeschooled' : '',
            type: which === 'homeschool' ? 'Home school' : '',
        });
        setManual(which);
    };

    // The full directory shows from the moment the dialog opens, like the real
    // form; typing narrows it. Rendering is capped at LOOKUP_PAGE rows.
    const q = query.trim().toLowerCase();
    const matches: DirectoryRow[] = [];
    let totalMatches = 0;
    if (open && !manual && rows) {
        if (!q) {
            totalMatches = rows.length;
            for (let i = 0; i < rows.length && matches.length < LOOKUP_PAGE; i++) matches.push(rows[i]);
        } else if (mode === 'state') {
            for (const r of rows) {
                if (r[2].toLowerCase() === q || r[2].toLowerCase().startsWith(q.slice(0, 2))) {
                    totalMatches++;
                    if (matches.length < LOOKUP_PAGE) matches.push(r);
                }
            }
        } else {
            const later: DirectoryRow[] = [];
            const col = mode === 'name' ? 0 : 1;
            for (const r of rows) {
                const n = r[col].toLowerCase();
                if (n.startsWith(q)) { totalMatches++; if (matches.length < LOOKUP_PAGE) matches.push(r); }
                else if (n.includes(q)) later.push(r);
            }
            totalMatches += later.length;
            for (const r of later) {
                if (matches.length >= LOOKUP_PAGE) break;
                matches.push(r);
            }
        }
    }

    // Manual-entry requirements, verified live against the real School Information
    // page (aria-required): name + country always; US → State + Zip Code required;
    // Canada → Province + Postal Code required; everywhere else State or Province +
    // Postal Code shown but optional. School Type is required, but only on the
    // not-listed path for high schools — the homeschool variant has no type field.
    const showType = kind === 'schools' && manual === 'notlisted';
    const isUS = m.country === 'United States';
    const isCA = m.country === 'Canada';
    const regionRequired = isUS || isCA;
    const manualValid = !!m.name.trim() && !!m.country && (!showType || !!m.type)
        && (!regionRequired || (!!m.region && !!m.postal.trim()));

    const commitManual = () => {
        if (!manualValid) return;
        const cityLine = [m.city.trim(), [m.region.trim(), m.postal.trim()].filter(Boolean).join(' ')]
            .filter(Boolean).join(', ');
        const composed = [
            m.name.trim(), m.line1.trim(), m.line2.trim(), m.line3.trim(), cityLine,
            isUS ? '' : m.country,   // US stays implicit, like the directory rows
        ].filter(Boolean).join(', ');
        onCommit(kind === 'schools' && m.type ? `${composed} (${m.type})` : composed);
        setOpen(false);
    };

    const dialog = open && (
        <div className='ffield__dlgBackdrop' onClick={() => setOpen(false)}>
            <div className='ffield__dlg' onClick={e => e.stopPropagation()} role='dialog' aria-modal='true'>
                <div className='ffield__dlgHead'>
                    <h3>{kind === 'schools' ? 'High School Lookup' : 'College Lookup'}</h3>
                    <button type='button' className='ffield__dlgClose' onClick={() => setOpen(false)} aria-label='Close'>
                        <MdClose size={18} />
                    </button>
                </div>

                {!manual && (
                    <div className='ffield__dlgBody'>
                        <div className='ffield__dlgSearchRow'>
                            <label className='ffield__dlgBy'>
                                Search by
                                <select value={mode} onChange={e => { setMode(e.target.value as LookupMode); setQuery(''); }}>
                                    <option value='name'>{kind === 'schools' ? 'High School Name' : 'College Name'}</option>
                                    <option value='city'>City</option>
                                    <option value='state'>State</option>
                                </select>
                            </label>
                            <div className='ffield__lookup ffield__dlgSearch'>
                                <MdSearch className='ffield__lookupIcon' />
                                <input
                                    className='ffield__input ffield__lookupInput'
                                    type='text'
                                    autoFocus
                                    value={query}
                                    placeholder={mode === 'name' ? `Type the ${noun} name`
                                        : mode === 'city' ? 'Type a city'
                                        : 'Type a state (e.g. NJ)'}
                                    onChange={e => setQuery(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className='ffield__dlgResults'>
                            {!rows && !error && <div className='ffield__lookupHint'>Loading directory…</div>}
                            {error && (
                                <div className='ffield__lookupHint'>
                                    Couldn’t load the {noun} directory.{' '}
                                    <button type='button' className='ffield__lookupBack' onClick={retry}>Try again</button>
                                </div>
                            )}
                            {rows && matches.length === 0 && <div className='ffield__lookupHint'>No matches</div>}
                            {matches.map((r, i) => (
                                <button
                                    key={i}
                                    type='button'
                                    className='ffield__lookupRow'
                                    onClick={() => { onCommit(r[0]); setOpen(false); }}
                                >
                                    <span className='ffield__lookupRowName'>{r[0]}</span>
                                    <span className='ffield__lookupRowMeta'>{r[1] ? `${r[1]}, ` : ''}{r[2]}</span>
                                </button>
                            ))}
                            {totalMatches > matches.length && (
                                <div className='ffield__lookupHint'>
                                    Showing {matches.length.toLocaleString()} of {totalMatches.toLocaleString()} — keep typing to narrow
                                </div>
                            )}
                        </div>

                        <div className='ffield__dlgFoot'>
                            {kind === 'schools' && (
                                <button type='button' className='ffield__lookupRow ffield__lookupRow--manual' onClick={() => openManual('homeschool')}>
                                    Homeschooled
                                </button>
                            )}
                            <button type='button' className='ffield__lookupRow ffield__lookupRow--manual' onClick={() => openManual('notlisted')}>
                                I don’t see my {kind === 'schools' ? 'high school' : 'college'} in this list
                            </button>
                        </div>
                    </div>
                )}

                {manual && (
                    <div className='ffield__dlgBody'>
                        <p className='ffield__dlgManualHint'>
                            {kind === 'schools' ? 'School Information' : 'College Information'}
                        </p>
                        <div className='ffield__dlgManualGrid'>
                            <label className='ffield__dlgManualField ffield__dlgManualField--wide'>
                                {kind === 'schools' ? 'High School Name' : 'College Name'} *
                                <input className='ffield__input' type='text' autoFocus value={m.name}
                                    onChange={e => setM({ ...m, name: e.target.value })} />
                            </label>
                            <label className='ffield__dlgManualField ffield__dlgManualField--wide'>
                                Country/Region/Territory *
                                <select className='ffield__input' value={m.country}
                                    onChange={e => setM({ ...m, country: e.target.value, region: '', postal: '' })}>
                                    <option value=''>- Choose an option -</option>
                                    {DIAL_CODES.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                                </select>
                            </label>
                            {showType && (
                                <label className='ffield__dlgManualField ffield__dlgManualField--wide'>
                                    School Type *
                                    <select className='ffield__input' value={m.type}
                                        onChange={e => setM({ ...m, type: e.target.value })}>
                                        <option value=''>- Choose an option -</option>
                                        {SCHOOL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </label>
                            )}
                            <label className='ffield__dlgManualField ffield__dlgManualField--wide'>
                                Address Line 1
                                <input className='ffield__input' type='text' value={m.line1}
                                    onChange={e => setM({ ...m, line1: e.target.value })} />
                            </label>
                            <label className='ffield__dlgManualField ffield__dlgManualField--wide'>
                                Address Line 2
                                <input className='ffield__input' type='text' value={m.line2}
                                    onChange={e => setM({ ...m, line2: e.target.value })} />
                            </label>
                            <label className='ffield__dlgManualField ffield__dlgManualField--wide'>
                                Address Line 3
                                <input className='ffield__input' type='text' value={m.line3}
                                    onChange={e => setM({ ...m, line3: e.target.value })} />
                            </label>
                            <label className='ffield__dlgManualField'>
                                City
                                <input className='ffield__input' type='text' value={m.city}
                                    onChange={e => setM({ ...m, city: e.target.value })} />
                            </label>
                            {m.country && (isUS || isCA ? (
                                <>
                                    <label className='ffield__dlgManualField ffield__dlgManualField--sm'>
                                        {isUS ? 'State *' : 'Province *'}
                                        <select className='ffield__input' value={m.region}
                                            onChange={e => setM({ ...m, region: e.target.value })}>
                                            <option value=''>--</option>
                                            {(isUS ? US_STATES : CA_PROVINCES).map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </label>
                                    <label className='ffield__dlgManualField ffield__dlgManualField--sm'>
                                        {isUS ? 'Zip Code *' : 'Postal Code *'}
                                        <input className='ffield__input' type='text' maxLength={10} value={m.postal}
                                            onChange={e => setM({ ...m, postal: e.target.value })} />
                                    </label>
                                </>
                            ) : (
                                <>
                                    <label className='ffield__dlgManualField'>
                                        State or Province
                                        <input className='ffield__input' type='text' value={m.region}
                                            onChange={e => setM({ ...m, region: e.target.value })} />
                                    </label>
                                    <label className='ffield__dlgManualField ffield__dlgManualField--sm'>
                                        Postal Code
                                        <input className='ffield__input' type='text' maxLength={12} value={m.postal}
                                            onChange={e => setM({ ...m, postal: e.target.value })} />
                                    </label>
                                </>
                            ))}
                        </div>
                        <div className='ffield__dlgManualActions'>
                            <button type='button' className='ffield__dlgBack' onClick={() => setManual(null)}>
                                Back
                            </button>
                            <button type='button' className='ffield__dlgContinue' disabled={!manualValid} onClick={commitManual}>
                                Continue
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    // Field-level display: chip with Change once a value exists, Find button before.
    return (
        <div>
            {value ? (
                <div className='ffield__lookupChosen'>
                    <span className='ffield__lookupChosenName'>{value}</span>
                    {!disabled && (
                        <span className='ffield__lookupChosenActions'>
                            <button type='button' className='ffield__lookupChange' onClick={openDialog}>Change</button>
                            <button type='button' className='ffield__lookupChange ffield__lookupRemove' onClick={() => onCommit('')}>Remove</button>
                        </span>
                    )}
                </div>
            ) : (
                <button type='button' className='ffield__lookupFind' disabled={disabled} onClick={openDialog}>
                    <MdSearch size={16} /> Find {noun === 'school' ? 'your school' : 'a college'}
                </button>
            )}
            {dialog}
        </div>
    );
}


function UploadControl({ value, disabled, onPick }: {
    value: string;
    disabled: boolean;
    onPick: (name: string) => void;
}) {
    const inputRef = useRef<HTMLInputElement>(null);
    return (
        <div className='ffield__upload'>
            <input
                ref={inputRef}
                type='file'
                className='ffield__uploadInput'
                disabled={disabled}
                onChange={e => {
                    const f = e.target.files && e.target.files[0];
                    if (f) onPick(f.name);
                }}
            />
            <button
                type='button'
                className='ffield__uploadBtn'
                disabled={disabled}
                onClick={() => inputRef.current?.click()}
            >
                <MdUploadFile /> Upload document
            </button>
            {value !== '' && (
                <span className='ffield__uploadName'>
                    {value}
                    {!disabled && (
                        <button
                            type='button'
                            className='ffield__uploadClear'
                            aria-label='Remove uploaded document'
                            onClick={() => {
                                if (inputRef.current) inputRef.current.value = '';
                                onPick('');
                            }}
                        >
                            <MdClose />
                        </button>
                    )}
                </span>
            )}
        </div>
    );
}

export default FillAppField;
